import { Card } from "../components/ui/card";
import { useState } from "react";
import { Rotate3D } from "lucide-react"; // ✅ Flip indicator icon
import gallery1 from "../assets/gallery-1.jpg";
import gallery2 from "../assets/gallery-2.jpg";
import gallery3 from "../assets/gallery-3.jpg";
import gallery4 from "../assets/gallery-4.jpg";
import gallery5 from "../assets/gallery-5.jpg";
import gallery6 from "../assets/gallery-6.jpg";
import heroImage from "../assets/hero-coldroom.jpg"; // ✅ Hero image

const Gallery = () => {
  const [flippedIndex, setFlippedIndex] = useState<number | null>(null);

  const galleryImages = [
    {
      src: gallery1,
      alt: "Coldroom Installation",
      title: "Professional Coldroom Installation",
      desc: "Custom-built coldrooms installed to spec with food-safe finishes and precise sealing.",
    },
    {
      src: gallery2,
      alt: "AC Unit Installation",
      title: "Commercial Air Conditioning",
      desc: "Energy-efficient split and ducted systems sized and commissioned for your space.",
    },
    {
      src: gallery3,
      alt: "Completed Coldroom",
      title: "Walk-in Coldroom Interior",
      desc: "Durable insulated panels, hygienic doors, and even airflow for consistent temps.",
    },
    {
      src: gallery4,
      alt: "Refrigeration Equipment",
      title: "Industrial Refrigeration System",
      desc: "High-capacity compressors, controls, and protection for 24/7 reliability.",
    },
    {
      src: gallery5,
      alt: "Commercial Kitchen",
      title: "Commercial Kitchen Cooling",
      desc: "Back-of-house cooling solutions designed for heavy daily use and easy cleaning.",
    },
    {
      src: gallery6,
      alt: "Office AC Installation",
      title: "Office Climate Control",
      desc: "Quiet, comfortable office environments with low running costs and smart control.",
    },
  ];

  const handleFlip = (index: number) => {
    setFlippedIndex((prev) => (prev === index ? null : index));
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="text-white py-20 bg-cover bg-center"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-center">
            Our Gallery
          </h1>
          <p className="text-xl text-center max-w-3xl mx-auto">
            Explore our completed projects and professional installations
          </p>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((image, index) => (
              <FlipCard
                key={index}
                image={image}
                flipped={flippedIndex === index}
                onFlip={() => handleFlip(index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Our Expertise</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto mb-8">
            <div>
              <div className="text-4xl font-bold text-primary mb-2">20+</div>
              <p className="text-muted-foreground">Years Experience</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary mb-2">500+</div>
              <p className="text-muted-foreground">Projects Completed</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary mb-2">100%</div>
              <p className="text-muted-foreground">Client Satisfaction</p>
            </div>
          </div>

          {/* Company Description */}
          <p className="text-muted-foreground max-w-3xl mx-auto text-center">
            With more than 20 years in operation, 3A Cold Rooms has built a strong reputation
            for reliability, technical excellence, and superior service quality. Our focus on
            innovation and precision has allowed us to expand our capabilities, handle large-scale
            projects, and deliver consistent value to our clients.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Gallery;

type GalleryItem = {
  src: string;
  alt: string;
  title: string;
  desc: string;
};

const FlipCard = ({
  image,
  flipped,
  onFlip,
}: {
  image: GalleryItem;
  flipped: boolean;
  onFlip: () => void;
}) => {
  return (
    <Card
      className="overflow-hidden cursor-pointer [perspective:1000px] group relative"
      onClick={onFlip}
    >
      <div
        className={
          "relative aspect-square transition-transform duration-500 [transform-style:preserve-3d]" +
          (flipped ? " [transform:rotateY(180deg)]" : "")
        }
      >
        {/* Front */}
        <div className="absolute inset-0 [backface-visibility:hidden] overflow-hidden">
          <img
            src={image.src}
            alt={image.alt}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          {/* Gradient and title */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-100 flex items-end">
            <div className="p-4 text-white">
              <h3 className="text-lg font-semibold">{image.title}</h3>
            </div>
          </div>

          {/* ✅ Hover overlay indicator */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="flex flex-col items-center text-white">
              <Rotate3D className="w-8 h-8 mb-2 animate-pulse" />
              <span className="text-xs uppercase tracking-wide">
                Click to Flip
              </span>
            </div>
          </div>
        </div>

        {/* Back */}
        <div className="absolute inset-0 bg-card text-card-foreground p-4 flex items-center justify-center text-center [transform:rotateY(180deg)] [backface-visibility:hidden]">
          <p className="text-sm text-muted-foreground max-w-sm">{image.desc}</p>
        </div>
      </div>
    </Card>
  );
};
